import { reactive } from 'vue'

export interface ConfirmOptions {
  title?: string
  message: string
  confirmText?: string
  cancelText?: string
  /** Style the confirm button as destructive (red). */
  danger?: boolean
}

interface ConfirmState {
  open: boolean
  title: string
  message: string
  confirmText: string
  cancelText: string
  danger: boolean
}

// Single shared dialog instance (mounted once via <ConfirmDialog /> in App.vue).
const state = reactive<ConfirmState>({
  open: false,
  title: '',
  message: '',
  confirmText: 'Confirm',
  cancelText: 'Cancel',
  danger: false,
})

let resolver: ((value: boolean) => void) | null = null

/**
 * Promise-based confirm dialog — a drop-in replacement for `window.confirm`.
 *
 *   const { confirm } = useConfirm()
 *   if (!(await confirm('Delete this?'))) return
 *   if (!(await confirm({ message: '…', danger: true, confirmText: 'Delete' }))) return
 */
export function useConfirm() {
  function confirm(opts: ConfirmOptions | string): Promise<boolean> {
    const o = typeof opts === 'string' ? { message: opts } : opts
    state.title = o.title ?? ''
    state.message = o.message
    state.confirmText = o.confirmText ?? 'Confirm'
    state.cancelText = o.cancelText ?? 'Cancel'
    state.danger = o.danger ?? false
    state.open = true
    return new Promise<boolean>((resolve) => { resolver = resolve })
  }

  // Used only by <ConfirmDialog />.
  function _resolve(value: boolean) {
    state.open = false
    resolver?.(value)
    resolver = null
  }

  return { confirm, state, _resolve }
}
